import {
  TILE_BORDER,
  TILE_DIM_BG,
  TILE_DIM_TEXT,
  TILE_END_SIZE,
  TILE_END_WEIGHT,
  TILE_FONT_FAMILY,
  TILE_LABEL_SIZE,
  TILE_LABEL_WEIGHT,
  TILE_RADIUS,
} from '../constants';

export type DrawTileArgs = {
  ctx: CanvasRenderingContext2D;
  // content-space rect (drawing happens after ctx.translate(-scrollLeft,-scrollTop))
  x: number;
  y: number;
  w: number;
  h: number;
  bg: string;
  textColor: string;
  label: string; // presentable id
  endLabel: string; // localized end date + time, e.g. "12.06.2026 14:30"
  // content-space x of the visible day-area edges (for label anchoring)
  visibleLeft: number;
  visibleRight: number;
  alpha: number; // collapse fade
  // Greyed out (a selection is active and this isn't the open/hovered tile).
  dimmed?: boolean;
  // Hovered → larger drop shadow to enhance the "pop" (scaling is applied by caller).
  elevated?: boolean;
  // Active rental that is a SERVICE trip → draw a small gear before the code.
  isServiceTrip?: boolean;
};

// Wrench (service-trip) glyph — Font Awesome's solid `wrench`, viewBox 0 0 512 512.
// Drawn from the SVG path via Path2D so it reads as a real wrench at ~10px and stays
// crisp at any DPR — no font/emoji dependency.
const WRENCH_PATH =
  'M507.73 109.1c-2.24-9.03-13.54-12.09-20.12-5.51l-74.36 74.36-67.88-11.31-11.31-67.88 74.36-74.36c6.62-6.62 3.43-17.9-5.66-20.16-47.38-11.74-99.55.91-136.58 37.93-39.64 39.64-50.55 97.1-34.05 147.2L18.74 402.76c-24.99 24.99-24.99 65.51 0 90.5 24.99 24.99 65.51 24.99 90.5 0l213.21-213.21c50.12 16.71 107.47 5.68 147.37-34.22 37.07-37.07 49.7-89.32 37.91-136.73z';
let wrenchPath: Path2D | null = null;

// Draw the wrench centred at (cx, cy), spanning ~2r, filled with `color`.
const drawWrench = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  color: string
): void => {
  if (!wrenchPath) wrenchPath = new Path2D(WRENCH_PATH);
  const size = r * 2.2;
  const scale = size / 512;
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  ctx.fill(wrenchPath);
  ctx.restore();
};

const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  const radius = Math.min(r, w / 2, h / 2);
  // roundRect is supported in the preview's Chrome; fall back just in case.
  if (typeof (ctx as any).roundRect === 'function') {
    ctx.beginPath();
    (ctx as any).roundRect(x, y, w, h, radius);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

// Truncate `text` with a trailing ellipsis so it fits within `maxWidth` (px) at the
// ctx's CURRENT font. Returns the original text when it already fits. (On hover the
// full value is shown via a tooltip — see useBookingCanvas/VehicleTimeline.)
const ellipsize = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string => {
  if (maxWidth <= 0) return '';
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ell = '…';
  let truncated = text;
  while (
    truncated.length > 0 &&
    ctx.measureText(truncated + ell).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return truncated.length ? truncated + ell : ell;
};

export const drawBookingTile = ({
  ctx,
  x,
  y,
  w,
  h,
  bg,
  textColor,
  label,
  endLabel,
  visibleLeft,
  visibleRight,
  alpha,
  dimmed = false,
  elevated = false,
  isServiceTrip = false,
}: DrawTileArgs): void => {
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

  // Dimmed tiles render in neutral grey (selection de-emphasis, no backdrop).
  const fillColor = dimmed ? TILE_DIM_BG : bg;
  const labelColor = dimmed ? TILE_DIM_TEXT : textColor;

  // Pill background — drawn with a soft drop shadow so tiles lift off the grid.
  // The shadow is applied ONLY to the fill; it's cleared before the border + text
  // so they stay crisp (otherwise the stroke/labels would smear). Hovered tiles
  // get a deeper shadow to enhance the scale-up "pop".
  roundedRectPath(ctx, x, y, w, h, TILE_RADIUS);
  ctx.shadowColor = elevated ? 'rgba(0, 0, 0, 0.24)' : 'rgba(0, 0, 0, 0.18)';
  ctx.shadowBlur = elevated ? 8 : 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = elevated ? 3 : 1;
  ctx.fillStyle = fillColor;
  ctx.fill();

  // Clear the shadow before stroking the border + drawing text.
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = 1;
  ctx.strokeStyle = TILE_BORDER;
  ctx.stroke();

  // Clip text to the pill
  roundedRectPath(ctx, x, y, w, h, TILE_RADIUS);
  ctx.clip();

  const padX = 8;
  const midY = y + h / 2;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = labelColor;

  // --- Presentable id: anchored to the visible LEFT edge, clamped to the pill,
  //     so it slides to stay readable as you scroll. Ellipsised to the pill interior
  //     when the tile is too small to show the whole code (full code on hover). ---
  // Service trips reserve room for a leading gear icon (drawn just left of the code,
  // travelling with it as the label slides).
  const gearR = TILE_LABEL_SIZE * 0.42;
  const gearSpace = isServiceTrip ? gearR * 2 + 5 : 0;

  ctx.font = `${TILE_LABEL_WEIGHT} ${TILE_LABEL_SIZE}px ${TILE_FONT_FAMILY}`;
  const shownLabel = ellipsize(ctx, label, w - padX * 2 - gearSpace);
  const labelW = ctx.measureText(shownLabel).width;
  // end-date label width (measured with its own font) reserves right-side room
  ctx.font = `${TILE_END_WEIGHT} ${TILE_END_SIZE}px ${TILE_FONT_FAMILY}`;
  const endW = ctx.measureText(endLabel).width;

  // Title offset: keep label near the left visible edge but inside the pill.
  const relLeft = visibleLeft - x; // how far the visible-left is into the pill
  const titleLeftBound = padX;
  const titleRightBound = Math.max(
    padX,
    w - labelW - endW - gearSpace - padX * 3
  );
  const titleOffset = clamp(relLeft, titleLeftBound, titleRightBound);
  // Gear icon rides just left of the code (same sliding offset).
  if (isServiceTrip) {
    drawWrench(ctx, x + titleOffset + gearR, midY, gearR, labelColor);
  }
  ctx.font = `${TILE_LABEL_WEIGHT} ${TILE_LABEL_SIZE}px ${TILE_FONT_FAMILY}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = labelColor;
  ctx.fillText(shownLabel, x + titleOffset + gearSpace, midY);

  // --- End-date label: rides the visible RIGHT edge inside the pill. ---
  // Only show it once the pill is wide enough to not collide with the title.
  if (w > labelW + endW + gearSpace + padX * 4) {
    const relRight = visibleRight - x;
    const endLeftBound = labelW + padX * 2 + endW; // don't overlap the title
    const endRightBound = w - padX;
    const endOffset = clamp(relRight, endLeftBound, endRightBound);
    ctx.font = `${TILE_END_WEIGHT} ${TILE_END_SIZE}px ${TILE_FONT_FAMILY}`;
    ctx.textAlign = 'right';
    ctx.fillText(endLabel, x + endOffset, midY);
  }

  ctx.restore();
};
